/** @jsx jsx */
import { Button } from '@datacamp/waffles/button';
import { Checkbox } from '@datacamp/waffles/checkbox';
import { Dialog } from '@datacamp/waffles/dialog';
import { FormField } from '@datacamp/waffles/form-field';
import { mediaQuery } from '@datacamp/waffles/helpers';
import { Text } from '@datacamp/waffles/text';
import { TextArea } from '@datacamp/waffles/text-area';
import { tokens } from '@datacamp/waffles/tokens';
import { jsx } from '@emotion/react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import getBackToDashboardSurvey from '../helpers/getBackToDashboardSurvey';
import { useDispatch, useSelector } from '../interfaces/State';
import { saveBackToDashboardSurvey } from '../redux/actions/dashboard';
import { selectTechnologyId } from '../redux/selectors';

type Props = {
  redirectUrl?: string;
};

const BackToDashboardSurvey: React.FC<Props> = ({ redirectUrl }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [comment, setComment] = useState('');

  const continueToDashboard = (event: any) => {
    dispatch(
      saveBackToDashboardSurvey({
        redirectUrl,
        checks: selectedItems,
        text: comment,
      }),
    );
    event.preventDefault();
  };

  const technologyId = useSelector(selectTechnologyId);
  const survey = getBackToDashboardSurvey(technologyId);
  React.useEffect(() => {
    if (survey == null) {
      // @ts-expect-error ts-migrate(2322) FIXME: Type 'undefined' is not assignable to type 'string... Remove this comment to see the full error message
      window.location.href = redirectUrl;
    }
  }, [survey, redirectUrl]);
  if (survey == null) return null;

  const handleCheckboxChange = (value: string): void => {
    const updatedValues = selectedItems.includes(value)
      ? selectedItems.filter((item) => {
          return item !== value;
        })
      : [...new Set(selectedItems.concat(value))];

    setSelectedItems(updatedValues);
  };

  return (
    <>
      <Dialog.Header>{t('BackToDashboardSurvey.header')}</Dialog.Header>
      <Dialog.Body>
        <fieldset
          css={{
            border: 0,
            padding: 0,
            margin: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: tokens.spacing.small,
            marginBottom: tokens.spacing.large,
            [mediaQuery.aboveSmall]: {
              minWidth: 500,
            },
          }}
        >
          <Text
            as="legend"
            css={{
              fontWeight: tokens.fontWeights.bold,
              marginBottom: tokens.spacing.medium,
            }}
          >
            {t('BackToDashboardSurvey.legend')}
          </Text>
          {survey.questions.map((question: any) => (
            <div key={question.key}>
              <Checkbox
                onChange={() => handleCheckboxChange(question.key)}
                checked={selectedItems.includes(question.key)}
              >
                {question.text}
              </Checkbox>
              {question.subQuestions.length > 0 &&
                selectedItems.includes(question.key) && (
                  <div
                    css={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: tokens.spacing.small,
                      paddingLeft: tokens.spacing.medium,
                      marginTop: tokens.spacing.small,
                    }}
                  >
                    {question.subQuestions.map((subQuestion: any) => (
                      <Checkbox
                        key={subQuestion.key}
                        onChange={() => handleCheckboxChange(subQuestion.key)}
                        checked={selectedItems.includes(subQuestion.key)}
                      >
                        {subQuestion.text}
                      </Checkbox>
                    ))}
                  </div>
                )}
            </div>
          ))}
        </fieldset>
        <FormField label="Something else you want to mention?">
          <TextArea
            name="BackToDashboardSurveyTextArea"
            value={comment}
            onChange={(event) => setComment(event.target.value)}
            rows={4}
          />
        </FormField>
      </Dialog.Body>
      <Dialog.Footer>
        <Button variant="secondary" onClick={continueToDashboard}>
          {t('BackToDashboardSurvey.button')}
        </Button>
      </Dialog.Footer>
    </>
  );
};

export default BackToDashboardSurvey;
